import { cn } from '~/lib/shadcnUtils';
import { OTPInput as OTPInputPrimitive, type SlotProps, REGEXP_ONLY_DIGITS } from 'input-otp';

interface OTPInputComponentProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  disabled?: boolean;
  onComplete?: (value: string) => void;
  hasError?: boolean;
}

interface SlotExtendedProps extends SlotProps {
  hasError?: boolean;
}

function Slot(props: SlotExtendedProps) {
  return (
    <div
      className={cn(
        'relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-lg border border-[#3B3B3E] bg-transparent font-medium text-[#333333] shadow-sm transition-all',
        {
          'z-10 border-2 border-[#333333] ring-2 ring-[#333333]/20': props.isActive && !props.hasError,
          'border-[#FF0000] text-[#333333] z-10 border-2 ring-2 ring-[#FF0000]/20': props.hasError,
        },
      )}
    >
      {props.char !== null && <div className="text-lg sm:text-xl">{props.char}</div>}
      {props.hasFakeCaret && props.char === null && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={cn('h-6 w-0.5 animate-pulse bg-[#333333]', props.hasError && 'bg-[#FF0000]')} />
        </div>
      )}
    </div>
  );
}

export function OtpInput({
  value,
  onChange,
  maxLength = 6,
  disabled = false,
  onComplete,
  hasError = false,
}: OTPInputComponentProps) {
  return (
    <OTPInputPrimitive
      value={value}
      onChange={(newValue) => {
        onChange(newValue);

        if (newValue.length === maxLength && onComplete) {
          onComplete(newValue);
        }
      }}
      maxLength={maxLength}
      disabled={disabled}
      pattern={REGEXP_ONLY_DIGITS}
      containerClassName="flex items-center justify-center gap-2 sm:gap-3 has-[:disabled]:opacity-50"
      render={({ slots }) => (
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex gap-2 sm:gap-3">
            {slots.map((slot, idx) => (
              <Slot key={idx} {...slot} hasError={hasError} />
            ))}
          </div>
          {hasError && (
            <div className="shrink-0">
              <div className="i-ph:warning-circle-fill text-[#FF0000] text-2xl" />
            </div>
          )}
        </div>
      )}
    />
  );
}
